"use client";
import { MAIN_TERM_PATHS_ARRAY } from "@/constants";
import Author from "@/ui/Author/Index";
import { Category } from "@/ui/Category/Index";
import ImageUI from "@/ui/Image/Index";
import { Summary } from "@/ui/Summary/Index";
import { Time } from "@/ui/Time/Index";
import { formatArabicDate } from "@/utils/formatDate";
import { getNewspaperDate } from "@/utils/getNewspaperDate";
import { cn } from "@pulsex/utils";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useMemo } from "react";
import { postHref } from "@/utils/links";

type Props = {
  data: any;
  widgetColor?: any;
  className?: string;
  isMobile?: boolean;
  widgetTitle?: string;
  cropSize?: string;
  resolution?: number;
  classNameSummary?: string;
};

export default function HorizontalCard({
  data,
  widgetColor,
  widgetTitle,
  className,
  classNameSummary,
  isMobile,
  cropSize = "wide",
  resolution,
}: Props) {
  const cardUrl = postHref(data, "/");
  const animatorMeta = data?.metas?.find(
    (item: any) => item.key === "animator" && item.value
  );
  const isWebsiteOnly = data?.metas?.find(
    (item: any) =>
      item.key === "website_only" &&
      (item.value === true || item.value === "true")
  );
  const TermValue =
    data?.terms?.[0]?.title === "على الغلاف"
      ? data?.terms?.[1]
      : data?.terms?.[0];

  const CategoryValue =
    data?.tag?.title !== widgetTitle?.trim() ? data?.tag : TermValue;

  const dateValue = getNewspaperDate(data);

  const dateToShow = useMemo(() => {
    let date: string | undefined;
    if (data?.publishDate && !dateValue) {
      date = formatArabicDate({
        arabicDate: data.publishDate,
        needDay: false,
      });
    } else if (data?.publishDate && dateValue) {
      date = formatArabicDate({
        arabicDate: dateValue || data.publishDate,
        needDay: false,
      });
    }
    return date;
  }, [data, dateValue]);

  const useMainTerm = (pathname: string) => {
    return MAIN_TERM_PATHS_ARRAY.indexOf(pathname) !== -1;
  };

  const pathname = usePathname();
  const decodedPathname = useMemo(() => decodeURI(pathname), [pathname]);
  const isMainTerm = useMainTerm(decodedPathname);

  const terms = data?.terms ?? [];
  const forceTerm = (() => {
    if (terms[0]?.title === "على الغلاف") {
      return terms[2];
    }

    if (
      terms.length > 2 &&
      ((terms[0]?.title === "عرب" &&
        terms[1]?.title !== "فلسطين" &&
        terms[1]?.title !== "سوريا") ||
        terms[0]?.title === "عالم")
    ) {
      return terms[2];
    }

    if (terms[0]?.title === "سوريا") {
      return terms[0];
    }

    if (terms.length < 3 && terms[1]?.title === "حياة وناس") {
      return terms[0];
    }

    return terms[1] ?? terms[0];
  })();

  return (
    <div className={cn("flex group", className)}>
      <div
        className={cn(
          "rounded-tr-3xl relative overflow-hidden",
          "max-md:w-[42%] md:w-[49%] lg:w-[35%] 2xl:w-[30%]"
        )}
      >
        <Category
          color={widgetColor || data?.tag?.color}
          item={isMainTerm && forceTerm ? forceTerm : CategoryValue}
          className={cn(
            "md:hidden absolute rounded-none rounded-tl-xl z-[2] mb-0 py-[1px] px-2",
            "max-md:right-0 max-md:bottom-0 max-md:text-lg max-md:flex gap-1"
          )}
          paintBg
          isWebsiteOnly={isWebsiteOnly}
        />

        <Link href={cardUrl || ""} prefetch={false}>
          <ImageUI
            data={data}
            cropSize={cropSize}
            resolution={resolution}
            imageClassName={"max-md:object-cover"}
            className="rounded-none w-full bg-greyBackgroundDim/60 max-md:h-full max-md:min-h-[128px]"
          />
        </Link>
      </div>
      <div
        className={cn(
          "flex flex-col flex-1 bg-lightgreyBackground",
          "group-hover:lg:bg-[#f9f9f9] group-hover:lg:shadow transition-all"
        )}
      >
        <Category
          color={widgetColor || data?.tag?.color}
          item={isMainTerm && forceTerm ? forceTerm : CategoryValue}
          className="max-md:hidden rounded-none rounded-bl-2xl py-1 lg:py-1.5 lg:pr-5 lg:mb-2 mb-1.5"
          paintBg
          isWebsiteOnly={isWebsiteOnly}
        />
        {!CategoryValue && !forceTerm ? (
          <>
            <div className="md:h-4 md:w-5 max-md:w-fit h-2" />
          </>
        ) : (
          ""
        )}
        <div className="px-4 pb-3 max-lg:pb-1.5 pt-1.5 max-lg:px-2 flex flex-col flex-1">
          <Link href={cardUrl || ""} prefetch={false} className="mb-2 max-md:mb-0">
            <h2
              className={cn(
                "cursor-pointer line-clamp-2 max-md:line-clamp-3 text-black",
                " md:leading-7 max-md:!leading-[20px] text-[19px] lg:text-[30px] "
              )}
            >
              {data?.title}
            </h2>
          </Link>

          {(data?.summary || data?.textContent) && (
            <Summary
              className={cn(
                "max-w-full max-md:text-[13px] font-noto-arabic text-base max-md:!leading-4",
                "line-clamp-2 text-[#545454] mb-1",
                classNameSummary
              )}
            >
              {data?.summary || data?.textContent}
            </Summary>
          )}

          <div
            className={cn(
              "flex items-end justify-between w-full h-fit mt-auto"
            )}
          >
            <Author post={data} className="max-sm:text-xs" />

            {data?.postType === "opinion-caricature" && animatorMeta && (
              <div
                className={cn(
                  "text-sm max-md:text-xs w-fit cursor-pointer text-minorTheme"
                )}
              >
                {animatorMeta.value}
              </div>
            )}

            {dateToShow && (
              <Time
                item={dateToShow}
                className={cn("max-md:leading-3 max-md:text-xs")}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
